import { evenlySplitWithCorrection, roundFloatToCents, roundFloatToCentsAsDollar } from "@hydra/internal";
import { floatVal, Nullable } from "@jamesgmarks/utilities";
import {
  AccordionDetails,
  AccordionSummary,
  Button,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Paper,
  Typography,
  Box,
  FormControlLabel,
  Stack,
  IconButton,
  Switch,
  Tooltip,
  Divider,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import {
  StyledAccordion,
  StyledTextField,
} from "src/components/pages/Subscriptions/tiered-unit-count/styledComponents";
import { BuildingDropdown } from "src/components/parts/BuildingDropdown";
import { StyledTableCell, StyledTableRow } from "src/components/parts/mui/StyledTables";
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';

interface IBuildingData {
  id: number;
  name: string;
  price: string;
}

interface ISubscriptionBuildingEditorProps {
  buildingsData: IBuildingData[];
  clientAccountId: Nullable<number>;
  onBuildingChanged: (building: { buildingName: string; id: number }, individualPrice: string) => void;
  onBuildingRemoved: (id: number) => void;
  onPriceChanged: (id: number, newPrice: string) => void;
  basePrice?: Nullable<number>;
  hasEvenBuildingSplit?: boolean;
  defaultEvenBuildingSplit?: boolean;
  onEvenBuildingSplitChanged?: (evenBuildingSplit: boolean) => void;
  disabled?: boolean;
}

export const SubscriptionBuildingEditor: React.FC<ISubscriptionBuildingEditorProps> = React.memo(({
  buildingsData,
  clientAccountId,
  onBuildingChanged,
  onBuildingRemoved,
  onPriceChanged,
  basePrice = null,
  hasEvenBuildingSplit = false,
  defaultEvenBuildingSplit = false,
  onEvenBuildingSplitChanged = () => { },
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [batchPrice, setBatchPrice] = useState<Nullable<string>>(`${basePrice ?? ''}`);

  useEffect(() => {
    if (basePrice && buildingsData.length > 0) {
      const pricePerBuilding = (basePrice / buildingsData.length);
      setBatchPrice(`${pricePerBuilding}`);
    }
  }, [basePrice, buildingsData.length]);

  const applyBatchPrice = () => {
    const parsed = floatVal(batchPrice ?? 0);
    if (Number.isNaN(parsed) || parsed < 0 || buildingsData.length === 0) return;
    buildingsData.forEach(({ id }) => {
      onPriceChanged(id, `${roundFloatToCents(parsed)}`);
    });
  };

  const forceApplyBasePrice = () => {
    if (!basePrice || buildingsData.length === 0) return;

    const corrected = evenlySplitWithCorrection(basePrice, buildingsData.length);

    buildingsData.forEach(({ id }, idx) => {
      onPriceChanged(id, `${corrected[idx]}`);
    });
  };

  const onBuildingAdded = (building: { buildingName: string; id: number }) => {
    if (basePrice) {
      const numberOfBuildings = buildingsData.length + 1;
      const pricePerBuilding = roundFloatToCents(basePrice / numberOfBuildings);
      setBatchPrice(`${pricePerBuilding}`);
      onBuildingChanged(building, `${pricePerBuilding}`);
      applyBatchPrice();
    } else {
      onBuildingChanged(building, batchPrice ?? '0');
    }
  };

  return (
    <StyledAccordion
      disabled={!clientAccountId || disabled}
      expanded={expanded && clientAccountId !== null}
      onChange={() => setExpanded((old) => !old)}
    >
      <AccordionSummary expandIcon={<GridExpandMoreIcon sx={{ fontSize: "2rem" }} />}>
        <Typography variant="h6">Manage buildings...</Typography>
      </AccordionSummary>

      <AccordionDetails>
        {hasEvenBuildingSplit && (
          <Box>
            <FormControlLabel
              label="Evenly split between ALL buildings"
              control={
                <Switch
                  checked={defaultEvenBuildingSplit}
                  onChange={(e) => onEvenBuildingSplitChanged(e.target.checked)}
                  color="primary"
                />
              }
            />
          </Box>
        )}

        {basePrice && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <StyledTextField
              size="small"
              label="Price to apply to all buildings"
              value={batchPrice}
              onChange={(e) => {
                setBatchPrice(e.target.value);
              }}
              sx={{ width: 250 }}
            />

            <Tooltip title="Round up to nearest cent">
              <IconButton
                color="primary"
                onClick={() => {
                  setBatchPrice(`${roundFloatToCents(floatVal(batchPrice ?? 0), 'up')}`);
                }}
                sx={{
                  p: 1.25,
                  '&:hover': {
                    backgroundColor: 'rgba(35, 102, 170, 0.1)',
                    borderRadius: 2,
                    color: 'primary.dark',
                  },
                }}
              >
                <ArrowCircleUpIcon fontSize="large" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Round down to nearest cent">
              <IconButton
                color="primary"
                onClick={() => {
                  setBatchPrice(`${roundFloatToCents(floatVal(batchPrice ?? 0), 'down')}`);
                }}
                sx={{
                  p: 1.25,
                  '&:hover': {
                    backgroundColor: 'rgba(35, 102, 170, 0.1)',
                    borderRadius: 2,
                    color: 'primary.dark',
                  },
                }}
              >
                <ArrowCircleDownIcon fontSize="large" />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={applyBatchPrice}
              sx={{ ml: 1 }}
            >
              Apply
            </Button>
            <Tooltip
              title={
                'Forcefully apply the base price to all buildings, '
                + 'trying the best to keep the total price equal to the base price.'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="medium"
                  onClick={forceApplyBasePrice}
                  disabled={basePrice === null}
                  sx={{ ml: 1 }}
                >
                  Force Apply Base Price
                </Button>
              </span>
            </Tooltip>

          </Stack>
        )}

        <BuildingDropdown
          accountId={clientAccountId ?? -1}
          onBuildingChanged={onBuildingAdded}
          hideOptionLabelOnSelect
        />

        <Divider sx={{ my: 2 }} />

        {
          buildingsData.length > 0
            ? <>
              <StyledTextField
                fullWidth
                size="small"
                label="Filter table for buildings..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <StyledTableRow>
                      <StyledTableCell><b>Name</b></StyledTableCell>
                      <StyledTableCell><b>ID</b></StyledTableCell>
                      <StyledTableCell><b>Price</b></StyledTableCell>
                      <StyledTableCell align="right"><b>Remove</b></StyledTableCell>
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {buildingsData
                      .filter(({ name, id }) =>
                        name.toLowerCase().includes(searchFilter.toLowerCase()) ||
                        String(id).includes(searchFilter))
                      .map(({ id, name, price }) => (
                        <StyledTableRow key={id} dark={true}>
                          <StyledTableCell dark={true}>{name}</StyledTableCell>
                          <StyledTableCell dark={true}>{id}</StyledTableCell>
                          <StyledTableCell dark={true}>
                            <StyledTextField
                              value={price}
                              placeholder="0.00"
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (/^\d*\.?\d{0,2}$/.test(raw)) {
                                  onPriceChanged(id, raw);
                                }
                              }}
                              onBlur={(e) => {
                                const raw = e.target.value;
                                const parsed = floatVal(raw);
                                if (!Number.isNaN(parsed)) {
                                  onPriceChanged(id, `${roundFloatToCents(parsed)}`);
                                }
                              }}
                              type="number"
                              size="small"
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{
                                '& .MuiInputBase-root': {
                                  backgroundColor: 'white',
                                  color: 'black',
                                },
                                '& input': {
                                  color: 'black',
                                },
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell dark={true} align="right">
                            <Button
                              variant="contained"
                              color="error"
                              onClick={() => onBuildingRemoved(id)}
                            >
                              X
                            </Button>
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    <StyledTableRow dark={true}>
                      <StyledTableCell colSpan={2} dark={true}><b>Total</b></StyledTableCell>
                      <StyledTableCell dark={true}>
                        <b>
                          {roundFloatToCentsAsDollar(
                            buildingsData.reduce(
                              (acc, { price }) => acc + floatVal(price),
                              0,
                            ),
                          )}
                        </b>
                      </StyledTableCell>
                      <StyledTableCell />
                    </StyledTableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
            : <Typography variant="body1" color="textSecondary" align="center">
              No buildings available. Please add a building to manage its price.
            </Typography>
        }
      </AccordionDetails>
    </StyledAccordion>
  );
});
